import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'
import type { BedbankEnquiryTotals } from 'checkout/lib/utils/bedbank/enquiryTotals'

const PROPERTY_FEES_MATCH_TOLERANCE = 0.01

export function hasPropertyRateUpgradeFeeMismatchBetweenRates(
  firstPropertyFees: number | undefined,
  secondPropertyFees: number | undefined,
): boolean {
  return firstPropertyFees !== undefined &&
    secondPropertyFees !== undefined &&
    Math.abs(firstPropertyFees - secondPropertyFees) > PROPERTY_FEES_MATCH_TOLERANCE
}

export function isPropertyRateLoyaltyPackageUpgradeItem(item: App.Checkout.BaseAccommodationItem<string>): boolean {
  return item.loyaltyAppliedUpgradeType === 'package' && !!item.loyaltyPropertyRateUpgrade
}

/**
 * Whether the item carries a property-rate package upgrade *regardless of applied state
 */
export function hasPropertyRateLoyaltyUpgrade(item: App.Checkout.BaseAccommodationItem<string>): boolean {
  return !!item.loyaltyPropertyRateUpgrade
}

export function getPropertyRateLoyaltyBasePackageUniqueKey(item: App.Checkout.BaseAccommodationItem<string>): string | undefined {
  const source = item.loyaltyPropertyRateUpgrade?.source
  if (!isPropertyRateLoyaltyPackageUpgradeItem(item) || !source?.servicePackageId) return
  return getPackageUniqueKey(source.servicePackageId, item.duration, source.serviceRateId)
}

export function findPropertyRateLoyaltyBasePackage(
  offer: App.Offer,
  item: App.Checkout.LEAccommodationItem,
): App.HotelPackage | undefined {
  const uniqueKey = getPropertyRateLoyaltyBasePackageUniqueKey(item)
  const pkg = uniqueKey ? offer.packages.find(pkg => pkg.uniqueKey === uniqueKey) : undefined
  return pkg && isHotelPackage(pkg) ? pkg : undefined
}

/**
 * One side of a loyalty package upgrade as item view totals.
 *
 * `surchargePayableAtProperty` is not read off `pricing`: an applied upgrade is charged at the
 * source price but stays in the *target* room, and the property collects its surcharge for the
 * room the guest occupies. Callers that disclose a surcharge pass the booked rate's figure;
 * the rest leave it at 0, as before.
 */
export function getPropertyRatePricingItemTotals(
  pricing: App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'],
  surchargePayableAtProperty: number = 0,
): App.Checkout.ItemViewTotals {
  return {
    price: pricing.totals.sellingPrice,
    memberPrice: pricing.luxPlusTotals?.sellingPrice ?? 0,
    value: pricing.totals.valuedUpToPrice,
    memberValue: pricing.luxPlusTotals?.valuedUpToPrice ?? 0,
    surcharge: 0,
    taxesAndFees: pricing.totals.taxesAndFees,
    extraGuestSurcharge: surchargePayableAtProperty,
    otherFees: {
      propertyFees: pricing.totals.propertyFees,
      ...(surchargePayableAtProperty ? { extraGuestSurcharge: surchargePayableAtProperty } : {}),
    },
    isAvailable: true,
  }
}

/**
 * The un-marked figures a bedbank enquiry sends for one side of a loyalty package upgrade.
 *
 * `getPropertyRatePricingItemTotals` is the marked, display-side counterpart. The enquiry travels
 * with a discount that is itself computed on the base basis, so the price has to be too, or
 * svc-bedbank validates a marked price against a base discount and checkout re-marks the result.
 */
export function getPropertyRatePricingEnquiryTotals(
  pricing: App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'],
): BedbankEnquiryTotals {
  return {
    price: readBaseSellingPrice(pricing.totals),
    memberPrice: pricing.luxPlusTotals ? readBaseSellingPrice(pricing.luxPlusTotals) : 0,
    propertyFees: pricing.totals.propertyFees,
  }
}

/**
 * A rate's un-marked selling price, tolerating quotes persisted before `baseSellingPrice` existed.
 *
 * redux-persist can restore a checkout cart saved before the channel-markup foundation shipped, whose
 * totals carry no `baseSellingPrice` despite the type. Those quotes predate channel markup, so the
 * marked `sellingPrice` *is* the base price — read it rather than send `undefined` (→ NaN) to a
 * pricing service, which would reject the enquiry.
 */
function readBaseSellingPrice(totals: App.PropertyRateTotals): number {
  return totals.baseSellingPrice ?? totals.sellingPrice
}

/**
 * The source-priced totals an applied upgrade is charged at.
 *
 * `surchargePayableAtProperty` is the *booked* (target) rate's property-collected surcharge — see
 * `getPropertyRatePricingItemTotals`. Omit it on paths that do not disclose one.
 */
export function getPropertyRateUpgradeSourceItemTotals(
  item: App.Checkout.BaseAccommodationItem<string>,
  surchargePayableAtProperty: number = 0,
): App.Checkout.ItemViewTotals | undefined {
  const pricing = item.loyaltyPropertyRateUpgrade?.sourcePricing
  return pricing ? getPropertyRatePricingItemTotals(pricing, surchargePayableAtProperty) : undefined
}

/**
 * Returns one side of a loyalty package upgrade's selling price using the customer's price basis.
 * LuxPlus+ subscribers use the member price when available, otherwise the public price.
 * `getPropertyRatePricingItemTotals().memberPrice` cannot be used because it represents a missing
 * member price as `0` for display.
 */
export function getPropertyRatePricingSellingPrice(
  pricing: App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'],
  isLuxPlusSubscriber: boolean,
): number {
  const memberPrice = pricing.luxPlusTotals?.sellingPrice
  return isLuxPlusSubscriber && memberPrice ? memberPrice : pricing.totals.sellingPrice
}

/**
 * As `getPropertyRatePricingSellingPrice`, but on the un-marked price basis.
 *
 * Use this for anything handed to a pricing service; use the marked variant for display.
 */
export function getPropertyRatePricingBaseSellingPrice(
  pricing: App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'],
  isLuxPlusSubscriber: boolean,
): number {
  const memberPrice = pricing.luxPlusTotals ? readBaseSellingPrice(pricing.luxPlusTotals) : undefined
  return isLuxPlusSubscriber && memberPrice ? memberPrice : readBaseSellingPrice(pricing.totals)
}

/**
 * Calculate the loyalty package upgrade discount sent with a bedbank enquiry.
 *
 * The discount is the difference between the target and source selling prices on the customer's
 * current subscription basis. Bedbank validates margin against that discounted price.
 *
 * Both sides are read un-marked, to match the un-marked price the discount travels with. Taking
 * the delta between marked prices instead scales it by the markup, and because checkout re-marks
 * `base - discount` the error is amplified again — enough to move the result a whole rounding step.
 */
export function getUpgradePricingDiscount(
  upgrade: Pick<App.Checkout.PropertyRateLoyaltyUpgrade, 'sourcePricing' | 'targetPricing'>,
  isLuxPlusSubscriber: boolean,
): number | undefined {
  const { sourcePricing, targetPricing } = upgrade
  if (!targetPricing) return

  const discount = floatify(
    getPropertyRatePricingBaseSellingPrice(targetPricing, isLuxPlusSubscriber) -
    getPropertyRatePricingBaseSellingPrice(sourcePricing, isLuxPlusSubscriber),
  )
  // A negative discount would push the effective price above what we're charging.
  return Math.max(discount, 0)
}

/**
 * Return the quoted total on the customer's current subscription basis.
 *
 * Read this before handling a rejected loyalty package upgrade. Forfeiting it replaces the source
 * price with the target rate's own price and removes the original quote.
 */
export function getQuotedUpgradeTotal(
  itemViews: Array<{ totals: Pick<App.Checkout.ItemViewTotals, 'price' | 'memberPrice'> }>,
  isLuxPlusSubscriber: boolean,
): number {
  return sum(itemViews, ({ totals }) => (isLuxPlusSubscriber && totals.memberPrice) || totals.price)
}

/**
 * Return whether changed enquiry fees break the property-fee parity required for a package upgrade.
 *
 * Availability pairs only source and target rates with matching property fees. Because a later
 * enquiry can reprice either side, compare its fees with the opposite side stored on the upgrade.
 * Bedbank returns fees for the whole session, so total the opposite side across the enquiry group.
 */
export function hasPropertyRateUpgradeFeeMismatch(
  items: Array<App.Checkout.BaseAccommodationItem<string>>,
  changedPropertyFees: number | undefined,
): boolean {
  if (changedPropertyFees === undefined) return false

  let hasOppositePricing = false
  const oppositePropertyFees = sum(items, (item) => {
    const upgrade = item.loyaltyPropertyRateUpgrade
    if (!upgrade) return 0
    const oppositePricing = upgrade.applied === false ? upgrade.targetPricing : upgrade.sourcePricing
    if (!oppositePricing) return 0
    hasOppositePricing = true
    return oppositePricing.totals.propertyFees
  })

  return hasOppositePricing && hasPropertyRateUpgradeFeeMismatchBetweenRates(
    changedPropertyFees,
    oppositePropertyFees,
  )
}

/**
 * Return the payable total and optional comparison for the loyalty package upgrade rejection modal.
 *
 * Bedbank returns only changed figures, so a LuxPlus+ response may contain only the member price.
 * Select the payable figure before building the comparison. Treat a zero member price as unavailable
 * and fall back to the standard price.
 *
 * Returning the comparison values together preserves type narrowing for `<PriceChangeViewV2/>`.
 */
export function getUpgradeRejectionPrices({ quotedPrice, newPrice, newLuxPlusPrice, isLuxPlusSubscriber }: {
  quotedPrice?: number
  newPrice?: number
  newLuxPlusPrice?: number
  isLuxPlusSubscriber: boolean
}): { payablePrice?: number, comparison?: { oldPrice: number, newPrice: number } } {
  const payablePrice = (isLuxPlusSubscriber && newLuxPlusPrice) || newPrice
  return {
    payablePrice,
    comparison: quotedPrice !== undefined && payablePrice !== undefined
      ? { oldPrice: quotedPrice, newPrice: payablePrice }
      : undefined,
  }
}

/**
 * Return the total shown after a rejected loyalty package upgrade is forfeited.
 *
 * A property-fee-only refusal contains no price. Fall back to the target rate's own price on the
 * customer's current subscription basis so the modal can still show the payable total.
 */
export function getForfeitedUpgradeTotals({ price, luxplusPrice, nonDiscountedPrice, isLuxPlusSubscriber }: {
  price?: number
  luxplusPrice?: number
  nonDiscountedPrice: number
  isLuxPlusSubscriber: boolean
}): { newPrice?: number, newLuxPlusPrice?: number } {
  return {
    newPrice: price ?? (isLuxPlusSubscriber ? undefined : nonDiscountedPrice),
    newLuxPlusPrice: luxplusPrice ?? (isLuxPlusSubscriber ? nonDiscountedPrice : undefined),
  }
}

export function getPropertyRateUpgradeDiscount(
  item: App.Checkout.BaseAccommodationItem<string>,
  isLuxPlusSubscriber: boolean,
): number | undefined {
  const upgrade = item.loyaltyPropertyRateUpgrade
  if (!upgrade || !isPropertyRateLoyaltyPackageUpgradeItem(item)) return
  return getUpgradePricingDiscount(upgrade, isLuxPlusSubscriber)
}
